/**
 * Mock Product Instance Data
 * 
 * Provides mock data for product instances for development and testing
 */

import type { ProductInstance, ProductType } from '@/types/product';

/**
 * Generate mock product instances for a company
 */
export function generateMockProductInstances(companyId: string, companyName: string): ProductInstance[] {
  const baseDate = new Date();
  
  // Determine which products to include based on company
  const instances: ProductInstance[] = [];
  
  // Most companies have Access Financials
  if (companyId === '1' || companyId === '4' || companyId === '6') {
    instances.push({
      id: `${companyId}-fin`,
      productType: 'access-financials',
      instanceName: `${companyName} Finance System`,
      companyId,
      connectionStatus: 'connected',
      apiEndpoint: `https://api.access-financials.com/${companyId}`,
      lastSync: new Date(baseDate.getTime() - 1000 * 60 * 30), // 30 minutes ago
      lastTestDate: new Date(baseDate.getTime() - 1000 * 60 * 60 * 2), // 2 hours ago
      lastTestStatus: 'success',
      syncFrequency: 'hourly',
      autoSync: true,
      config: {
        syncProjects: true,
        syncCostCenters: true,
        syncInvoices: true,
      },
      errorMessage: null,
      autoConnectedByInstanceId: null,
      createdAt: new Date(baseDate.getTime() - 1000 * 60 * 60 * 24 * 90), // 90 days ago
      updatedAt: new Date(baseDate.getTime() - 1000 * 60 * 30),
    });
  }
  
  // Some companies have People HR
  if (companyId === '1' || companyId === '2' || companyId === '4') {
    instances.push({
      id: `${companyId}-hr`,
      productType: 'people-hr',
      instanceName: `${companyName} HR System`,
      companyId,
      connectionStatus: 'connected',
      apiEndpoint: `https://api.peoplehr.com/${companyId}`,
      lastSync: new Date(baseDate.getTime() - 1000 * 60 * 15), // 15 minutes ago
      lastTestDate: new Date(baseDate.getTime() - 1000 * 60 * 60), // 1 hour ago
      lastTestStatus: 'success',
      syncFrequency: 'daily',
      autoSync: true,
      config: {
        syncEmployees: true,
        syncAbsences: true,
        syncLeaveBalances: true,
      },
      errorMessage: null,
      autoConnectedByInstanceId: null,
      createdAt: new Date(baseDate.getTime() - 1000 * 60 * 60 * 24 * 85), // 85 days ago
      updatedAt: new Date(baseDate.getTime() - 1000 * 60 * 15),
    });
  }
  
  // Company 1 (Acme) has full suite
  if (companyId === '1') {
    instances.push(
      {
        id: `${companyId}-dim`,
        productType: 'access-dimensions',
        instanceName: `${companyName} Dimensions`,
        companyId,
        connectionStatus: 'connected',
        apiEndpoint: `https://api.access-dimensions.com/${companyId}`,
        lastSync: new Date(baseDate.getTime() - 1000 * 60 * 45), // 45 minutes ago
        lastTestDate: new Date(baseDate.getTime() - 1000 * 60 * 60 * 3), // 3 hours ago
        lastTestStatus: 'success',
        syncFrequency: 'hourly',
        autoSync: true,
        config: {
          syncDimensions: true,
          syncCostCenters: true,
        },
        errorMessage: null,
        autoConnectedByInstanceId: null,
        createdAt: new Date(baseDate.getTime() - 1000 * 60 * 60 * 24 * 60), // 60 days ago
        updatedAt: new Date(baseDate.getTime() - 1000 * 60 * 45),
      },
      {
        id: `${companyId}-proj`,
        productType: 'access-projects',
        instanceName: `${companyName} Projects`,
        companyId,
        connectionStatus: 'connected',
        apiEndpoint: `https://api.access-projects.com/${companyId}`,
        lastSync: new Date(baseDate.getTime() - 1000 * 60 * 20), // 20 minutes ago
        lastTestDate: new Date(baseDate.getTime() - 1000 * 60 * 60 * 4), // 4 hours ago
        lastTestStatus: 'success',
        syncFrequency: 'hourly',
        autoSync: true,
        config: {
          syncProjects: true,
          syncTasks: true,
          syncMilestones: true,
        },
        errorMessage: null,
        autoConnectedByInstanceId: null,
        createdAt: new Date(baseDate.getTime() - 1000 * 60 * 60 * 24 * 75), // 75 days ago
        updatedAt: new Date(baseDate.getTime() - 1000 * 60 * 20),
      }
    );
  }
  
  // Company 3 (Gamma) has error state
  if (companyId === '3') {
    instances.push({
      id: `${companyId}-fin`,
      productType: 'access-financials',
      instanceName: `${companyName} Finance System`,
      companyId,
      connectionStatus: 'error',
      apiEndpoint: `https://api.access-financials.com/${companyId}`,
      lastSync: new Date(baseDate.getTime() - 1000 * 60 * 60 * 24 * 2), // 2 days ago
      lastTestDate: new Date(baseDate.getTime() - 1000 * 60 * 30), // 30 minutes ago
      lastTestStatus: 'failed',
      syncFrequency: 'hourly',
      autoSync: true,
      config: {},
      errorMessage: 'Authentication failed: Invalid API key',
      autoConnectedByInstanceId: null,
      createdAt: new Date(baseDate.getTime() - 1000 * 60 * 60 * 24 * 30), // 30 days ago
      updatedAt: new Date(baseDate.getTime() - 1000 * 60 * 30),
    });
  }
  
  // Company 6 (Zeta) has HR error
  if (companyId === '6') {
    instances.push({
      id: `${companyId}-hr`,
      productType: 'people-hr',
      instanceName: `${companyName} HR System`,
      companyId,
      connectionStatus: 'error',
      apiEndpoint: `https://api.peoplehr.com/${companyId}`,
      lastSync: new Date(baseDate.getTime() - 1000 * 60 * 60 * 24), // 1 day ago
      lastTestDate: new Date(baseDate.getTime() - 1000 * 60 * 45), // 45 minutes ago
      lastTestStatus: 'failed',
      syncFrequency: 'daily',
      autoSync: true,
      config: {},
      errorMessage: 'Connection timeout: Unable to reach HR system',
      autoConnectedByInstanceId: null,
      createdAt: new Date(baseDate.getTime() - 1000 * 60 * 60 * 24 * 45), // 45 days ago
      updatedAt: new Date(baseDate.getTime() - 1000 * 60 * 45),
    });
  }
  
  return instances;
}

/**
 * Get all product instances across all companies
 */
export function getAllMockProductInstances(): ProductInstance[] {
  const companyData = [
    { id: '1', name: 'Acme Corporation' },
    { id: '2', name: 'Beta Limited' },
    { id: '3', name: 'Gamma Industries' },
    { id: '4', name: 'Delta Enterprises' },
    { id: '5', name: 'Epsilon Solutions' },
    { id: '6', name: 'Zeta Technologies' },
  ];
  
  return companyData.flatMap(company => 
    generateMockProductInstances(company.id, company.name)
  );
}

/**
 * Get product instances for a specific company
 */
export function getMockProductInstancesForCompany(companyId: string): ProductInstance[] {
  const allInstances = getAllMockProductInstances();
  return allInstances.filter(instance => instance.companyId === companyId);
}

/**
 * Get a single product instance by ID
 */
export function getMockProductInstance(instanceId: string): ProductInstance | undefined {
  const allInstances = getAllMockProductInstances();
  return allInstances.find(instance => instance.id === instanceId);
}

/**
 * Simulate adding a new product instance
 */
export function createMockProductInstance(
  companyId: string,
  productType: ProductType,
  instanceName: string
): ProductInstance {
  const now = new Date();
  
  return {
    id: `${companyId}-${productType}-${Date.now()}`,
    productType,
    instanceName,
    companyId,
    connectionStatus: 'not_configured',
    apiEndpoint: null,
    lastSync: null,
    lastTestDate: null,
    lastTestStatus: null,
    syncFrequency: 'daily',
    autoSync: false,
    config: {},
    errorMessage: null,
    autoConnectedByInstanceId: null,
    createdAt: now,
    updatedAt: now,
  };
}
